/**
 * Application feature registrations.
 *
 * This is the single file where developers define which features are gated and
 * which plan is required to access them. Import this file once at app startup
 * (it is imported as a side-effect in `App.tsx`) — after that, all components
 * can query access via `useFeatureAccess` or `FeatureGate`.
 *
 * Access modes per tier:
 *   'full'   — feature works normally.
 *   'locked' — feature is shown in UI but blocked; clicking shows an upgrade prompt.
 *   'hidden' — feature is not rendered at all.
 *
 * To add a new gated feature:
 *   1. Give it a unique key string.
 *   2. Call `registerFeature({ key, description, access: { free, pro, teams } })`.
 *   3. Use `useFeatureAccess(key)` or `<FeatureGate featureKey={key}>` in the component.
 */

import { registerFeature } from './featureRegistry';

// ─── AI ──────────────────────────────────────────────────────────────────────

registerFeature({
    key: 'ai_slicing',
    description: 'AI-powered opportunity/story slicing suggestions',
    access: {
        free: 'locked',
        pro: 'full',
        teams: 'full',
    },
});

registerFeature({
    key: 'ai_estimation',
    description: 'AI-powered repo architecture scan and story point estimation',
    access: {
        free: 'locked',
        pro: 'full',
        teams: 'full',
    },
});

// ─── Project management ──────────────────────────────────────────────────────

registerFeature({
    key: 'export_zey',
    description: 'Export projects as .zey files',
    access: {
        free: 'locked',
        pro: 'full',
        teams: 'full',
    },
});

registerFeature({
    key: 'import_zey',
    description: 'Import projects from .zey files',
    access: {
        free: 'locked',
        pro: 'full',
        teams: 'full',
    },
});

// ─── Insights ────────────────────────────────────────────────────────────────

registerFeature({
    key: 'insights_panel',
    description: 'Insights collection and linking panel',
    access: {
        free: 'full',
        pro: 'full',
        teams: 'full',
    },
});

registerFeature({
    key: 'insights_ai_summary',
    description: 'AI-powered insights summary generation',
    access: {
        free: 'locked',
        pro: 'full',
        teams: 'full',
    },
});

// ─── GitHub integration ──────────────────────────────────────────────────────

registerFeature({
    key: 'github_push',
    description: 'Push user stories as GitHub issues',
    access: {
        free: 'locked',
        pro: 'full',
        teams: 'full',
    },
});
